/**
 * The conversational run-guidance agent (agent-astral's `astral.k8s.yaml`),
 * which diagnoses and repairs a paused quill2 run in the run-page chat.
 */
export const RAIN_ASTRAL2_AGENT_ID = 'astral2';

/**
 * The report-only exception agent (agent-astral-lite). It stores a structured
 * report that Astral forwards to the Workbench, and does not repair the run.
 */
export const RAIN_ASTRAL_LITE_AGENT_ID = 'astral-lite';

/**
 * The tools astral2 surfaces as chat cards. The three `custom_remote` ones block
 * the agent loop until the browser submits a result, so each renders an
 * interactive card rather than progress:
 *   - `request_connection`     — the shared connection picker.
 *   - `request_review`         — two modes, told apart by its arguments (see
 *     `detectReviewMode`): a `verify_checkpoint` approve/reject gate
 *     (`checkpoint_facts`), and a `verify_extraction` IDP field review
 *     (`source` + `extracted_fields`/`missing_fields`).
 *   - `request_guide_approval` — proposes a pending resolution-guide entry by
 *     resource name and blocks on approve/reject.
 *
 * Names MUST match the tool definitions in agent-astral's `astral.k8s.yaml`.
 */
export enum EAstralVisibleTool {
  // custom_remote — the browser must resolve these.
  RequestConnection = 'request_connection',
  RequestReview = 'request_review',
  RequestGuideApproval = 'request_guide_approval',
  // BDK — finding the app behind a failed step.
  SearchApps = 'search_apps',
}

/**
 * astral2's remaining tools: procedure lookup, plus the Astral source it drives
 * to diagnose and repair a paused run. These render no card — their outcomes
 * reach the user through the run page's resolution-guide widgets, the
 * resolved-exception list, and run state transitions.
 *
 * `apply_code_patch`, `retry_step` and `skip_step` mutate the run, so whether the
 * transcript should show them is a product decision.
 */
export enum EAstralInternalTool {
  SearchAppProcedures = 'search_app_procedures',
  ApplyCodePatch = 'apply_code_patch',
  RetryStep = 'retry_step',
  SkipStep = 'skip_step',
  ClassifyException = 'classify_exception',
  CreateResolutionGuide = 'create_resolution_guide',
  UpdateResolutionGuide = 'update_resolution_guide',
  GetResolutionEntry = 'get_resolution_entry',
  StoreExceptionResolution = 'store_exception_resolution',
  ListExceptionResolutions = 'list_exception_resolutions',
  SearchAutomationResolutions = 'search_automation_resolutions',
}

/**
 * `quill_handoff` hands Quill the context to fix an automation's SOURCE. astral2
 * calls it on a draft run when a defect cannot be worked around for that run.
 *
 * NOTHING IN THIS CLIENT RESOLVES IT. It is `custom_remote`, so the agent loop
 * blocks until the browser posts a result — but there is no card and no
 * `onRemoteToolCall`, so `maybeFulfillRemoteToolCall` returns at its first guard
 * and the turn stalls. A fix needs a resolver and a handoff UX.
 */
export enum EAstralUnhandledTool {
  QuillHandoff = 'quill_handoff',
}
